import { Tenant }                       from '@app/modules/admin-tenant/models/tenant.model';
import { AccountTenantService }         from '@app/modules/admin-tenant/services/account-tenant.service';
import { AdminTenantService }           from '@app/modules/admin-tenant/services/admin-tenant.service';
import { BusinessPremises }             from '@app/modules/business-premises/models/business-premises.model';
import { BusinessPremisesService }      from '@app/modules/business-premises/services/business-premises.service';
import {
	ErsPresetEnum,
	ErsVariant
}                                       from '@app/modules/ers/models/ers.model';
import { ErsService }                   from '@app/modules/ers/services/ers.service';
import { Organization }                 from '@app/modules/organization/models/organization.model';
import { OrganizationService }          from '@app/modules/organization/services/organization.service';
import {
	TssPresetEnum,
	TssVariant
}                                       from '@app/modules/tss/models/tss.model';
import { TssService }                   from '@app/modules/tss/services/tss.service';
import { User }                         from '@app/modules/user/models/user.model';
import { UserService }                  from '@app/modules/user/services/user.service';
import { IEntityMessages }              from '@core/interfaces/entity.interface';
import { PresetType }                   from '@models/preset';
import { ApplicationScopeTypes }        from '@models/scope.enums';
import { ActionEnum }                   from '@shared/components/action-item/action-item.enum';
import { ListOperationsIdentifierEnum } from '@shared/list-operations/model/list-operations.model';
import { actionStatus }                 from '@shared/services/toaster.service';


// ENUMS #############################################################
export enum OutOfFlowDisplayModeEnum {
	DISABLED = 'disabled',
	HIDDEN = 'hidden'
}

export enum StatusFlowTextModeEnum {
	HTML = 'html',
	PLAIN = 'plain',
	JSON = 'json'
}

// INTERFACES ########################################################
export interface IStatusFlowActionItem {
	action: ActionEnum;
	actionItemIcon?: ActionEnum;
	labelI18n: string;
	messages?: IEntityMessages;
	tooltipI18n: string;
	scopes: ApplicationScopeTypes | ApplicationScopeTypes[];
}

export interface IStatusFlowChangeEvent<T> {
	dialog: {
		i18nPathButtonCancel?: string;
		i18nPathButtonConfirm: string;
		i18nPathDescription: string;
	};

	requestActionStatus: actionStatus;
	targetStatus: T;
}

export interface IStatusFlowAction<T, Scope extends ApplicationScopeTypes> extends IStatusFlowChangeEvent<T> {
	actionItem: IStatusFlowActionItem;
	enforceDisableOnStatuses: T[];
	enforceHideOnStatuses: T[];
	order: number;
	outOfFlowDisplayMode: OutOfFlowDisplayModeEnum;
}

export interface IStatusFlowAllAction<T, Scope extends ApplicationScopeTypes> extends IStatusFlowAction<T, Scope> {
	isDisabled?: boolean;
	isHidden?: boolean;
}

/**
 * This is the description of the interface
 *
 * @stateToActionMap {Map<string, ActionEnum>} use this map for statuses only, where the status value differs from the action value
 * Type definition of the Map: <string: StatusEnum of Entities, ActionEnum>
 * e.g.: StatusEnum value: decommissioned and ActionEnum value: decommission
 * @actions {IStatusFlowAction<T>} config of actionItem and visibility
 * @flow { key: string ]: T[]} key is used for current state, value contains next statuses
 */
export interface IStatusFlow<T, Scope extends ApplicationScopeTypes> {
	actions: IStatusFlowAction<T, Scope>[];
	flow: {
		/** key is used for current state (Entities StatusEnum) */
		[ key: string ]: T[] // key is Entities StatusEnum, but in typescript cannot be that type
	};
}

export interface IStatusFlowDialogText {
	type: StatusFlowTextModeEnum;
	text: string;
}


export interface IStatusFlowOptions extends IChangeStatusCallback {
	callback: ( service: StatusFlowEntityServiceType, callbackOptions: ChangeStatusCallbackOptionType, presetId?: PresetType ) => void;
	callbackOptions: ChangeStatusCallbackOptionType;
	caller: string;
	descriptionSuffix?: string;
	enforceDialogText?: IStatusFlowDialogText;
	entity: StatusFlowEntityType;
	entityId: string;
	entityService;
	statusChangeEvent?: IStatusFlowChangeEvent<string>;
}

interface IChangeStatusBaseCallbackOption {
	entity: StatusFlowEntityType;
	caller?: string;
	presetId?: PresetType;
}

export interface IChangeStatusCallback {
	callback: ( service: StatusFlowEntityServiceType, callbackOptions: ChangeStatusCallbackOptionType ) => void;
}

export interface IChangeStatusBusinessPremisesCallbackOptions extends IChangeStatusBaseCallbackOption {
	listOperationsId?: ListOperationsIdentifierEnum;
}

export interface IChangeStatusErsCallbackOptions extends IChangeStatusBaseCallbackOption {
	listOperationsId?: ListOperationsIdentifierEnum;
	presetId: ErsPresetEnum;
}

export interface IChangeStatusOrganizationCallbackOptions extends IChangeStatusBaseCallbackOption {
	listOperationsId?: ListOperationsIdentifierEnum;
}

// tslint:disable-next-line:no-empty-interface
export interface IChangeStatusTenantCallbackOptions extends IChangeStatusBaseCallbackOption {}

// tslint:disable-next-line:no-empty-interface
export interface IChangeStatusTssCallbackOptions extends IChangeStatusBaseCallbackOption {
	listOperationsId?: ListOperationsIdentifierEnum;
	navigateToList?: boolean;
	presetId: TssPresetEnum;
}

// tslint:disable-next-line:no-empty-interface
export interface IChangeStatusUserCallbackOptions extends IChangeStatusBaseCallbackOption {}


// TYPES #############################################################
export type ChangeStatusCallbackOptionType =
	IChangeStatusBusinessPremisesCallbackOptions
	| IChangeStatusErsCallbackOptions
	| IChangeStatusOrganizationCallbackOptions
	| IChangeStatusTenantCallbackOptions
	| IChangeStatusTssCallbackOptions
	| IChangeStatusUserCallbackOptions;

export type StatusFlowEntityType =
	BusinessPremises
	| ErsVariant
	| Organization
	| Tenant
	| TssVariant
	| User;

export type StatusFlowEntityServiceType =
	AccountTenantService
	| AdminTenantService
	| BusinessPremisesService
	| ErsService
	| OrganizationService
	| TssService
	| UserService;

// ENTITIES ##########################################################
